import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getFromStorage } from '../utils/storage';
import { STORAGE_KEYS } from '../utils/constants';

// Stats categories for gamification
export const STAT_CATEGORIES = {
  STAMINA: 'stamina',     // Physical activities, sleep
  AGILITY: 'agility',     // Quick responses, reflexes
  AWARENESS: 'awareness', // Social interactions, QR shares
  MASTERY: 'mastery',     // Learning, achievements
  FOCUS: 'focus',         // Productivity, tasks completed
  REPUTATION: 'reputation', // Blockchain identity verification
  INNOVATION: 'innovation', // New features used
  SECURITY: 'security',   // Security practices
};

// Achievement types
export const ACHIEVEMENT_TYPES = {
  QR_NINJA: 'qr_ninja',           // 50+ QR scans
  TRUSTED_SHARER: 'trusted_sharer', // Create and share QRs
  PRODUCTIVITY_MASTER: 'productivity_master',
  SOCIAL_BUTTERFLY: 'social_butterfly',
  HEALTH_TRACKER: 'health_tracker',
  STREAK_KEEPER: 'streak_keeper',
  BLOCKCHAIN_PIONEER: 'blockchain_pioneer', // Early blockchain adopter
  IDENTITY_GUARDIAN: 'identity_guardian', // Identity verification
  CRYPTO_COLLECTOR: 'crypto_collector', // Token collection
  SECURE_VAULT: 'secure_vault', // Security features used
  COMMUNITY_BUILDER: 'community_builder', // Community participation
};

// Achievement rarity levels
export const ACHIEVEMENT_RARITY = {
  COMMON: 'common',
  UNCOMMON: 'uncommon',
  RARE: 'rare',
  EPIC: 'epic',
  LEGENDARY: 'legendary',
};

// Interface for the unified stats
/**
 * @typedef {Object} UnifiedStats
 * @property {string} decentralizedId
 * @property {number} level
 * @property {number} xp
 * @property {number} nextLevelXp
 * @property {Object} stats
 * @property {number} stats.stamina
 * @property {number} stats.agility
 * @property {number} stats.awareness
 * @property {number} stats.mastery
 * @property {number} stats.focus
 * @property {number} stats.reputation
 * @property {number} stats.innovation
 * @property {number} stats.security
 * @property {Array<Object>} achievements
 * @property {Object} qrStats
 * @property {Object} activityStats
 * @property {Object} healthStats
 * @property {Object} productivityStats
 * @property {Object} blockchainStats
 * @property {boolean} recentLevelUp
 * @property {Array<string>} recentAchievements
 * @property {Array<Object>} badges
 * @property {Array<Object>} milestones
 */

/**
 * Calculate level and next level XP from total XP
 * @param {number} xp - Total experience points
 * @returns {{level: number, nextLevelXp: number}} Level information
 */
const calculateLevel = (xp) => {
  const baseXp = 1000;
  const growthFactor = 1.5;
  
  let level = 1;
  let nextLevelRequirement = baseXp;
  let remainingXp = xp;
  
  while (remainingXp >= nextLevelRequirement) {
    remainingXp -= nextLevelRequirement;
    level += 1;
    nextLevelRequirement = Math.floor(baseXp * Math.pow(growthFactor, level - 1));
  }
  
  return { level, nextLevelXp: nextLevelRequirement };
};

export const useUnifiedStats = () => {
  const { t } = useTranslation();
  const [unifiedStats, setUnifiedStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Process QR Stats
  const processQRStats = () => {
    const customQRs = getFromStorage(STORAGE_KEYS.CUSTOM_LINKS, []);

    // Start with a default stats object
    const stats = {
      totalQRs: 0,
      totalViews: 0,
      weeklyGrowth: 0,
      categoryData: [],
      topQRs: []
    };
    
    if (!customQRs || customQRs.length === 0) {
      return stats;
    }
    
    // Calculate core statistics
    stats.totalQRs = customQRs.length;
    stats.totalViews = customQRs.reduce((total, link) => total + (link.totalVisits || 0), 0);
    
    // Calculate weekly growth
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    
    let lastWeekViews = 0;
    let currentWeekViews = 0;
    
    customQRs.forEach(link => {
      if (link.visits && Array.isArray(link.visits)) {
        link.visits.forEach(visit => {
          const visitDate = new Date(visit.timestamp);
          if (visitDate >= oneWeekAgo) {
            currentWeekViews++;
          } else {
            const twoWeeksAgo = new Date(oneWeekAgo);
            twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 7);
            if (visitDate >= twoWeeksAgo && visitDate < oneWeekAgo) {
              lastWeekViews++;
            }
          }
        });
      }
    });
    
    if (lastWeekViews > 0) {
      stats.weeklyGrowth = Math.round(((currentWeekViews - lastWeekViews) / lastWeekViews) * 100);
    } else if (currentWeekViews > 0) {
      stats.weeklyGrowth = 100; // If there were no views last week but there are this week, growth is 100%
    }
    
    // Category distribution
    const categoryCounts = {};
    const categoryColors = {
      general: '#4f46e5',  // Blue-Purple
      social: '#06b6d4',   // Cyan
      work: '#10b981',     // Green
      personal: '#ef4444', // Red
    };
    
    customQRs.forEach(link => {
      const category = link.category || 'general';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });

    // Convert to the format needed for charts
    stats.categoryData = Object.keys(categoryCounts).map(category => {
      const categoryName = category === 'general' ? t('common.general', 'Genel') :
                          category === 'social' ? t('common.social', 'Sosyal Medya') :
                          category === 'work' ? t('common.work', 'İş') :
                          category === 'personal' ? t('common.personal', 'Kişisel') : category;
      
      return {
        name: categoryName,
        value: categoryCounts[category],
        color: categoryColors[category] || '#4f46e5'
      };
    });
    
    // Most viewed QR codes
    stats.topQRs = [...customQRs]
      .sort((a, b) => (b.totalVisits || 0) - (a.totalVisits || 0))
      .slice(0, 5)
      .map(link => ({
        name: link.name,
        views: link.totalVisits || 0,
        id: link.id,
        url: link.url,
        category: link.category
      }));
      
    return stats;
  };
  
  // Mocked data for other statistics that would come from real APIs
  const getMockHealthStats = () => ({
    sleepAvg: 7.2,
    steps: 8432,
    active: 64, // minutes
    heartRate: 68,
  });
  
  const getMockProductivityStats = () => ({
    tasksCompleted: 12,
    tasksTotal: 15,
    focusMinutes: 240,
    pomodoroCycles: 6
  });

  // Get blockchain stats (mocked for now)
  const getMockBlockchainStats = () => ({
    verifications: 2,
    appConnections: 3,
    tokenCount: 1,
    lastVerification: new Date(Date.now() - 86400000 * 5), // 5 days ago
    securityScore: 75,
    totalTransactions: 12,
    transactionHistory: [
      { date: new Date(Date.now() - 86400000 * 10), type: 'verification', status: 'completed' },
      { date: new Date(Date.now() - 86400000 * 7), type: 'connection', status: 'completed' },
      { date: new Date(Date.now() - 86400000 * 3), type: 'token', status: 'completed' }
    ]
  });

  const generateAchievements = (qrStats, healthStats, productivityStats) => {
    const achievements = [
      {
        id: 'qr_ninja_1',
        type: ACHIEVEMENT_TYPES.QR_NINJA,
        name: t('achievements.qrNinja', 'QR Ninja'),
        description: t('achievements.qrNinjaDesc', 'Tarama ustası: 50 QR taraması yap'),
        earned: qrStats.totalViews >= 50,
        earnedAt: qrStats.totalViews >= 50 ? new Date() : undefined,
        progress: Math.min(qrStats.totalViews, 50),
        maxProgress: 50,
        icon: 'qrcode',
        rarity: ACHIEVEMENT_RARITY.COMMON,
        xpReward: 100
      },
      {
        id: 'trusted_sharer_1',
        type: ACHIEVEMENT_TYPES.TRUSTED_SHARER,
        name: t('achievements.trustedSharer', 'Güvenilir Paylaşımcı'),
        description: t('achievements.trustedSharerDesc', 'En az 10 QR kodunu paylaş'),
        earned: qrStats.totalQRs >= 10,
        earnedAt: qrStats.totalQRs >= 10 ? new Date() : undefined,
        progress: Math.min(qrStats.totalQRs, 10),
        maxProgress: 10,
        icon: 'share',
        rarity: ACHIEVEMENT_RARITY.COMMON,
        xpReward: 150
      },
      {
        id: 'productivity_master_1',
        type: ACHIEVEMENT_TYPES.PRODUCTIVITY_MASTER,
        name: t('achievements.productivityMaster', 'Üretkenlik Ustası'),
        description: t('achievements.productivityMasterDesc', 'Günde en az 3 görevi tamamla'),
        earned: productivityStats.tasksCompleted >= 10,
        earnedAt: productivityStats.tasksCompleted >= 10 ? new Date() : undefined,
        progress: Math.min(productivityStats.tasksCompleted, 10),
        maxProgress: 10,
        icon: 'check-circle',
        rarity: ACHIEVEMENT_RARITY.COMMON,
        xpReward: 200
      },
      {
        id: 'health_tracker_1',
        type: ACHIEVEMENT_TYPES.HEALTH_TRACKER,
        name: t('achievements.healthTracker', 'Sağlık İzleyici'),
        description: t('achievements.healthTrackerDesc', 'Günlük adım hedefini 5 gün üst üste yakala'),
        earned: healthStats.steps >= 8000,
        earnedAt: healthStats.steps >= 8000 ? new Date() : undefined,
        progress: Math.min(healthStats.steps, 8000),
        maxProgress: 8000,
        icon: 'activity',
        rarity: ACHIEVEMENT_RARITY.COMMON,
        xpReward: 100
      },
      // New blockchain and identity achievements
      {
        id: 'blockchain_pioneer_1',
        type: ACHIEVEMENT_TYPES.BLOCKCHAIN_PIONEER,
        name: t('achievements.blockchainPioneer', 'Blockchain Öncüsü'),
        description: t('achievements.blockchainPioneerDesc', 'Decentralized ID\'nizi oluşturun ve 3 farklı uygulamada kullanın'),
        earned: false, // Would be checked with actual blockchain interactions
        earnedAt: undefined,
        progress: 1, // Mocked progress
        maxProgress: 3,
        icon: 'box',
        rarity: ACHIEVEMENT_RARITY.UNCOMMON,
        xpReward: 250
      },
      {
        id: 'identity_guardian_1',
        type: ACHIEVEMENT_TYPES.IDENTITY_GUARDIAN,
        name: t('achievements.identityGuardian', 'Kimlik Koruyucusu'),
        description: t('achievements.identityGuardianDesc', 'Hesabınızda iki faktörlü kimlik doğrulamayı etkinleştirin'),
        earned: true, // Mocked as earned for demo
        earnedAt: new Date(Date.now() - 86400000 * 2), // 2 days ago
        progress: 1,
        maxProgress: 1,
        icon: 'shield',
        rarity: ACHIEVEMENT_RARITY.UNCOMMON,
        xpReward: 300
      },
      {
        id: 'crypto_collector_1',
        type: ACHIEVEMENT_TYPES.CRYPTO_COLLECTOR,
        name: t('achievements.cryptoCollector', 'Kripto Koleksiyoncusu'),
        description: t('achievements.cryptoCollectorDesc', 'İlk dijital varlığınızı cüzdanınıza ekleyin'),
        earned: false,
        earnedAt: undefined,
        progress: 0,
        maxProgress: 1,
        icon: 'dollar-sign',
        rarity: ACHIEVEMENT_RARITY.RARE,
        xpReward: 350
      },
      {
        id: 'secure_vault_1',
        type: ACHIEVEMENT_TYPES.SECURE_VAULT,
        name: t('achievements.secureVault', 'Güvenli Kasa'),
        description: t('achievements.secureVaultDesc', 'Dijital kasanızda 5 gizli belge saklayın'),
        earned: false,
        earnedAt: undefined,
        progress: 2,
        maxProgress: 5,
        icon: 'lock',
        rarity: ACHIEVEMENT_RARITY.RARE,
        xpReward: 400
      },
      {
        id: 'community_builder_1',
        type: ACHIEVEMENT_TYPES.COMMUNITY_BUILDER,
        name: t('achievements.communityBuilder', 'Topluluk Kurucu'),
        description: t('achievements.communityBuilderDesc', '10 farklı kişiyle güvenli kimlik bağlantısı kurun'),
        earned: false,
        earnedAt: undefined,
        progress: 3,
        maxProgress: 10,
        icon: 'users',
        rarity: ACHIEVEMENT_RARITY.EPIC,
        xpReward: 500
      }
    ];
    
    return achievements;
  };
  
  // Calculate total XP based on all activities
  const calculateXP = (qrStats, healthStats, productivityStats, blockchainStats) => {
    // Base XP from core activities
    const qrXP = qrStats.totalViews * 10 + qrStats.totalQRs * 50;
    const healthXP = healthStats.steps / 100 + healthStats.sleepAvg * 100;
    const productivityXP = productivityStats.tasksCompleted * 100 + productivityStats.focusMinutes;
    
    // New blockchain XP calculations
    const blockchainXP = (blockchainStats?.verifications || 0) * 200 + 
                        (blockchainStats?.appConnections || 0) * 100 +
                        (blockchainStats?.tokenCount || 0) * 50;
    
    return Math.floor(qrXP + healthXP + productivityXP + blockchainXP);
  };

  // Main load function that aggregates all stats
  const loadAllStats = async () => {
    try {
      setIsRefreshing(true);
      setError(null);
      
      // Process QR statistics
      const qrStats = processQRStats();
      
      // Get health, productivity, and blockchain stats (mocked for now)
      const healthStats = getMockHealthStats();
      const productivityStats = getMockProductivityStats();
      const blockchainStats = getMockBlockchainStats();
      
      // Calculate total XP
      const totalXP = calculateXP(qrStats, healthStats, productivityStats, blockchainStats);
      
      // Calculate level
      const { level, nextLevelXp } = calculateLevel(totalXP);
      
      // Generate achievements
      const achievements = generateAchievements(qrStats, healthStats, productivityStats);
      
      // Calculate achievement XP bonuses
      const earnedAchievements = achievements.filter(a => a.earned);
      const achievementXP = earnedAchievements.reduce((total, achievement) => total + (achievement.xpReward || 0), 0);
      
      // Check if there are any newly earned achievements (would be persisted in real app)
      const recentAchievements = achievements
        .filter(a => a.earned && new Date().getTime() - (a.earnedAt?.getTime() || 0) < 86400000) // last 24h
        .map(a => a.id);
      
      // Mock activity stats
      const activityStats = {
        dailyStreak: Math.min(30, Math.floor(totalXP / 1000)),
        totalActions: Math.floor(totalXP / 10),
        avgDailyActions: Math.floor(totalXP / 300),
        mostActiveDay: 'Wednesday',
        activityData: [
          { name: 'QR Usage', value: 30 },
          { name: 'Messaging', value: 25 },
          { name: 'Digital Vault', value: 15 },
          { name: 'Profile Updates', value: 10 },
          { name: 'Security Checks', value: 20 }
        ]
      };

      // Generate milestone badges
      const milestones = [
        {
          id: 'early_adopter',
          name: t('milestones.earlyAdopter', 'Erken Benimseyen'),
          description: t('milestones.earlyAdopterDesc', 'İlk 1000 kullanıcı arasında yer alın'),
          earned: true,
          earnedAt: new Date(Date.now() - 86400000 * 30), // 30 days ago
          icon: 'award',
          color: '#FFD700' // Gold
        },
        {
          id: 'identity_verified',
          name: t('milestones.identityVerified', 'Kimliği Doğrulanmış'),
          description: t('milestones.identityVerifiedDesc', 'Kimliğinizi blockchain üzerinde doğrulayın'),
          earned: blockchainStats.verifications > 0,
          earnedAt: blockchainStats.verifications > 0 ? blockchainStats.lastVerification : undefined,
          icon: 'check-circle',
          color: '#00CED1' // Turquoise
        },
        {
          id: 'blockchain_master',
          name: t('milestones.blockchainMaster', 'Blockchain Ustası'),
          description: t('milestones.blockchainMasterDesc', '10+ blockchain işlemi gerçekleştirin'),
          earned: blockchainStats.totalTransactions >= 10,
          earnedAt: blockchainStats.totalTransactions >= 10 ? new Date() : undefined,
          icon: 'box',
          color: '#9370DB' // Purple
        }
      ];

      // Combine all stats
      const unifiedData = {
        decentralizedId: 'did:ethr:0x1234...5678',
        level,
        xp: totalXP + achievementXP,
        nextLevelXp,
        stats: {
          [STAT_CATEGORIES.STAMINA]: Math.floor((healthStats.sleepAvg / 10) * 100),
          [STAT_CATEGORIES.AGILITY]: Math.floor((productivityStats.focusMinutes / 500) * 100),
          [STAT_CATEGORIES.AWARENESS]: Math.floor((qrStats.totalViews / 100) * 100),
          [STAT_CATEGORIES.MASTERY]: Math.floor((productivityStats.tasksCompleted / 20) * 100),
          [STAT_CATEGORIES.FOCUS]: Math.floor((healthStats.active / 120) * 100),
          [STAT_CATEGORIES.REPUTATION]: Math.floor(blockchainStats.securityScore || 0),
          [STAT_CATEGORIES.INNOVATION]: Math.floor((blockchainStats.appConnections / 5) * 100),
          [STAT_CATEGORIES.SECURITY]: Math.floor((blockchainStats.verifications / 3) * 100)
        },
        achievements,
        qrStats,
        activityStats,
        healthStats,
        productivityStats,
        blockchainStats,
        milestones,
        recentLevelUp: false, // Would track this in a real app
        recentAchievements,
        badges: milestones.filter(m => m.earned)
      };
      
      setUnifiedStats(unifiedData);
    } catch (err) {
      setError(err.message || 'Failed to load statistics');
      console.error('Error loading stats:', err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadAllStats();
  }, []);

  return {
    stats: unifiedStats,
    isLoading,
    isRefreshing,
    error,
    refreshStats: loadAllStats
  };
};